/**
 * Adversary Deployment Planning API Client
 *
 * Thin client for the /adversary/* backend routes (red-force IADS laydowns):
 * doctrine families/templates, the adversary system catalog, versioned
 * laydown persistence, and the advisor/narrative endpoints.
 *
 * Mirrors WeaponeeringAPIClient's auth pattern exactly (on-demand Auth0 token
 * provider with cached-token fallback) so protected endpoints don't 403 on a
 * transiently-expired token.
 */

import { apiErrorFromResponse } from '../apiError';

class AdversaryAPIClient {
  constructor(baseURL = null) {
    const baseUrl = baseURL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
    this.baseURL = `${baseUrl}/api`;
    this.authToken = null;
    this.tokenProvider = null;
  }

  setAuthToken(token) { this.authToken = token; }
  clearAuthToken() { this.authToken = null; }
  setTokenProvider(fn) { this.tokenProvider = fn; }
  clearTokenProvider() { this.tokenProvider = null; }

  async resolveToken() {
    if (this.tokenProvider) {
      try {
        const token = await this.tokenProvider();
        if (token) {
          this.authToken = token;
          return token;
        }
      } catch (err) {
        console.warn('⚠️ Could not obtain a fresh access token:', err?.message || err);
      }
    }
    return this.authToken;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    const token = await this.resolveToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
      const response = await fetch(url, { ...options, headers });
      if (!response.ok) {
        throw await apiErrorFromResponse(response);
      }
      if (response.status === 204) return null;
      return await response.json();
    } catch (error) {
      console.error(`Adversary API Error [${endpoint}]:`, error.message);
      throw error;
    }
  }

  // ==================== Doctrine ====================

  async listDoctrineFamilies() {
    return this.request('/adversary/doctrine-families');
  }

  async listDoctrineTemplates({ family = null, strategyKey = null } = {}) {
    const params = new URLSearchParams();
    if (family) params.set('family', family);
    if (strategyKey) params.set('strategy_key', strategyKey);
    const qs = params.toString();
    return this.request(`/adversary/doctrine-templates${qs ? `?${qs}` : ''}`);
  }

  async getDoctrineTemplate(templateRef) {
    return this.request(`/adversary/doctrine-templates/${encodeURIComponent(templateRef)}`);
  }

  // ==================== System catalog ====================

  async listSystemTypes({ family = null, category = null } = {}) {
    const params = new URLSearchParams();
    if (family) params.set('family', family);
    if (category) params.set('category', category);
    const qs = params.toString();
    return this.request(`/adversary/system-types${qs ? `?${qs}` : ''}`);
  }

  async createCustomSystemType(payload) {
    return this.request('/adversary/system-types', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // ==================== Laydowns ====================

  async listLaydowns({ missionId = null, status = null } = {}) {
    const params = new URLSearchParams();
    if (missionId) params.set('mission_id', missionId);
    if (status) params.set('status', status);
    const qs = params.toString();
    return this.request(`/adversary/laydowns${qs ? `?${qs}` : ''}`);
  }

  async saveLaydown(payload) {
    return this.request('/adversary/laydowns', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getLaydown(laydownUuid) {
    return this.request(`/adversary/laydowns/${laydownUuid}`);
  }

  async acceptLaydown(laydownUuid, acceptedBy = null) {
    return this.request(`/adversary/laydowns/${laydownUuid}/accept`, {
      method: 'POST',
      body: JSON.stringify({ accepted_by: acceptedBy }),
    });
  }

  async rejectLaydown(laydownUuid) {
    return this.request(`/adversary/laydowns/${laydownUuid}/reject`, { method: 'POST' });
  }

  async deleteLaydown(laydownUuid) {
    return this.request(`/adversary/laydowns/${laydownUuid}`, { method: 'DELETE' });
  }

  async createLaydownVersion(laydownUuid, payload) {
    return this.request(`/adversary/laydowns/${laydownUuid}/versions`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async listLaydownVersions(laydownUuid) {
    return this.request(`/adversary/laydowns/${laydownUuid}/versions`);
  }

  async revertLaydown(lineageId, targetVersion, { dryRun = false } = {}) {
    return this.request(`/adversary/laydowns/${lineageId}/revert`, {
      method: 'POST',
      body: JSON.stringify({ target_version: targetVersion, dry_run: dryRun }),
    });
  }

  // ==================== Emplacement CRUD (ad-hoc builder) ====================

  async addEmplacement(laydownUuid, emplacement) {
    return this.request(`/adversary/laydowns/${laydownUuid}/emplacements`, {
      method: 'POST',
      body: JSON.stringify(emplacement),
    });
  }

  async updateEmplacement(laydownUuid, emplacementId, emplacement) {
    return this.request(`/adversary/laydowns/${laydownUuid}/emplacements/${emplacementId}`, {
      method: 'PATCH',
      body: JSON.stringify(emplacement),
    });
  }

  async deleteEmplacement(laydownUuid, emplacementId) {
    return this.request(`/adversary/laydowns/${laydownUuid}/emplacements/${emplacementId}`, {
      method: 'DELETE',
    });
  }

  // ==================== Advisor / narrative ====================

  async askAdvisor(question, context = {}, history = []) {
    return this.request('/adversary/advisor', {
      method: 'POST',
      body: JSON.stringify({ question, context, history }),
    });
  }

  async generateNarrative(laydownUuid) {
    return this.request(`/adversary/laydowns/${laydownUuid}/narrative`, { method: 'POST' });
  }
}

const adversaryAPI = new AdversaryAPIClient();
export default adversaryAPI;
export { AdversaryAPIClient };
